/* ==========================================================================
   THE GEAR SPA — global.js
   Header, mega-menu, mobile drawer, cart drawer (AJAX), accordions,
   reveal-on-scroll, quantity steppers. Vanilla ES, defer-loaded.
   All motion respects prefers-reduced-motion; all controls keyboard-accessible.
   ========================================================================== */
(function () {
  'use strict';
  const RM = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const money = (c) => {
    try { return new Intl.NumberFormat('en-GB', { style: 'currency', currency: window.TGS?.currency || 'GBP' }).format(c / 100); }
    catch { return '£' + (c / 100).toFixed(2); }
  };

  /* ---------- Focus trap helper ---------- */
  function trap(container) {
    const sel = 'a[href],button:not([disabled]),input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])';
    function onKey(e) {
      if (e.key !== 'Tab') return;
      const f = $$(sel, container).filter((el) => el.offsetParent !== null);
      if (!f.length) return;
      const first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    container.addEventListener('keydown', onKey);
    return () => container.removeEventListener('keydown', onKey);
  }

  /* ---------- Header scroll state ---------- */
  const header = $('[data-header]');
  if (header) {
    let last = 0;
    const onScroll = () => {
      const y = window.scrollY;
      header.classList.toggle('is-scrolled', y > 24);
      last = y;
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ---------- Desktop mega-menu ---------- */
  $$('[data-mega-trigger]').forEach((trigger) => {
    const id = trigger.getAttribute('aria-controls');
    const panel = id && document.getElementById(id);
    if (!panel) return;
    const parent = trigger.closest('[data-mega-item]');
    let open = false; let timer;
    const set = (state) => {
      open = state; trigger.setAttribute('aria-expanded', String(state));
      parent.classList.toggle('is-open', state);
    };
    const show = () => { clearTimeout(timer); set(true); };
    const hide = () => { timer = setTimeout(() => set(false), 120); };
    parent.addEventListener('mouseenter', show);
    parent.addEventListener('mouseleave', hide);
    trigger.addEventListener('click', (e) => { e.preventDefault(); set(!open); });
    trigger.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { set(false); trigger.focus(); }
      if (e.key === 'ArrowDown') { e.preventDefault(); show(); const f = $('a,button', panel); f && f.focus(); }
    });
    panel.addEventListener('keydown', (e) => { if (e.key === 'Escape') { set(false); trigger.focus(); } });
    document.addEventListener('click', (e) => { if (open && !parent.contains(e.target)) set(false); });
    // Close when keyboard focus leaves the menu entirely (Tab past the last link)
    parent.addEventListener('focusout', (e) => { if (open && !parent.contains(e.relatedTarget)) set(false); });
  });

  /* ---------- Mobile navigation drawer ---------- */
  const mnav = $('[data-mobile-nav]');
  const mnavOpen = $('[data-mobile-open]');
  const mnavClose = $('[data-mobile-close]');
  let releaseNav;
  function openNav() {
    if (!mnav) return;
    mnav.hidden = false; requestAnimationFrame(() => mnav.classList.add('is-open'));
    document.body.classList.add('no-scroll'); mnavOpen?.setAttribute('aria-expanded', 'true');
    releaseNav = trap(mnav); ($('[data-mobile-close]', mnav) || mnav).focus();
  }
  function closeNav() {
    if (!mnav) return;
    mnav.classList.remove('is-open'); document.body.classList.remove('no-scroll');
    mnavOpen?.setAttribute('aria-expanded', 'false'); releaseNav && releaseNav();
    const done = () => { mnav.hidden = true; mnav.removeEventListener('transitionend', done); };
    RM ? done() : mnav.addEventListener('transitionend', done);
    mnavOpen?.focus();
  }
  mnavOpen?.addEventListener('click', openNav);
  mnavClose?.addEventListener('click', closeNav);
  mnav?.addEventListener('click', (e) => { if (e.target === mnav) closeNav(); });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && mnav && !mnav.hidden) closeNav(); });
  // Mobile accordion drill-down
  $$('[data-mnav-toggle]', mnav || document).forEach((btn) => {
    btn.addEventListener('click', () => {
      const sub = btn.nextElementSibling; const on = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', String(!on));
      if (sub) { sub.style.height = on ? '0px' : sub.scrollHeight + 'px'; }
    });
  });

  /* ---------- Accordions (generic) ---------- */
  function initAccordion(root) {
    $$('.accordion__trigger', root).forEach((btn) => {
      const controlsId = btn.getAttribute('aria-controls');
      const panel = (controlsId && document.getElementById(controlsId)) || btn.nextElementSibling;
      btn.addEventListener('click', () => {
        const on = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!on));
        if (!panel) return;
        if (on) { panel.style.height = panel.scrollHeight + 'px'; requestAnimationFrame(() => (panel.style.height = '0px')); }
        else { panel.style.height = panel.scrollHeight + 'px'; const clear = () => { panel.style.height = 'auto'; panel.removeEventListener('transitionend', clear); }; RM ? (panel.style.height = 'auto') : panel.addEventListener('transitionend', clear); }
      });
    });
  }
  initAccordion(document);

  /* ---------- Reveal on scroll ---------- */
  const reveals = $$('.reveal');
  if (reveals.length && !RM && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); } });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
    reveals.forEach((el) => io.observe(el));
  } else { reveals.forEach((el) => el.classList.add('is-in')); }

  /* ---------- Quantity steppers ---------- */
  $$('[data-qty]').forEach((wrap) => {
    const input = $('input', wrap);
    $$('[data-qty-btn]', wrap).forEach((b) => b.addEventListener('click', () => {
      const step = b.dataset.qtyBtn === 'inc' ? 1 : -1;
      const min = parseInt(input.min || '1', 10);
      input.value = Math.max(min, (parseInt(input.value, 10) || min) + step);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }));
  });

  /* ---------- Cart drawer + AJAX add ---------- */
  const drawer = $('[data-cart-drawer]');
  let releaseCart;
  async function refreshCart() {
    try {
      const r = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await r.json();
      $$('[data-cart-count]').forEach((el) => { el.textContent = cart.item_count; el.hidden = cart.item_count === 0; });
      const body = $('[data-cart-body]');
      if (body) body.innerHTML = renderCart(cart);
      const foot = $('[data-cart-foot]');
      if (foot) foot.hidden = cart.item_count === 0;
      const empty = $('[data-cart-empty]');
      if (empty) empty.hidden = cart.item_count !== 0;
      const notice = $('[data-cart-notice]');
      if (notice) notice.hidden = cart.item_count === 0;
      $$('[data-cart-total]').forEach((el) => (el.textContent = money(cart.total_price)));
      bindCartLineEvents();
    } catch (e) { /* silent */ }
  }
  // Properties that are plumbing rather than customer-facing detail.
  const HIDDEN_PROPS = ['In-person drop-off acknowledged', 'Consent'];
  const visibleProps = (props) =>
    Object.entries(props || {}).filter(([k, v]) => v && !k.startsWith('_') && HIDDEN_PROPS.indexOf(k) === -1);

  function renderCart(cart) {
    if (!cart.items.length) return '';
    return cart.items.map((it) => `
      <div class="cart-line" data-line-key="${it.key}">
        <a class="cart-line__media" href="${it.url}">${it.image ? `<img src="${it.image.replace(/(\.[a-z]+)(\?|$)/, '_160x$1$2')}" alt="" width="72" height="90" loading="lazy">` : ''}</a>
        <div class="cart-line__info">
          <a class="cart-line__title" href="${it.url}">${it.product_title}</a>
          ${it.variant_title && it.variant_title !== 'Default Title' ? `<p class="cart-line__variant">${it.variant_title}</p>` : ''}
          ${it.unit_price_measurement ? `<p class="cart-line__unit">${money(it.price)} / ${it.unit_price_measurement.reference_unit || 'unit'}</p>` : ''}
          ${visibleProps(it.properties).length ? `<ul class="cart-line__props">${visibleProps(it.properties).map(([k, v]) => `<li><span>${k}:</span> ${v}</li>`).join('')}</ul>` : ''}
          <div class="cart-line__row">
            <div class="qty qty--sm" data-qty>
              <button type="button" data-line-dec aria-label="Decrease quantity">–</button>
              <input type="text" value="${it.quantity}" data-line-qty aria-label="Quantity" readonly>
              <button type="button" data-line-inc aria-label="Increase quantity">+</button>
            </div>
            <span class="cart-line__price">${money(it.final_line_price)}</span>
          </div>
          <button class="cart-line__remove" data-line-remove type="button">Remove</button>
        </div>
      </div>`).join('');
  }
  async function changeLine(key, qty) {
    const r = await fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: key, quantity: qty }) });
    await r.json(); refreshCart();
  }
  function bindCartLineEvents() {
    $$('[data-line-key]').forEach((line) => {
      const key = line.dataset.lineKey;
      const qtyEl = $('[data-line-qty]', line);
      $('[data-line-inc]', line)?.addEventListener('click', () => changeLine(key, (parseInt(qtyEl.value, 10) || 1) + 1));
      $('[data-line-dec]', line)?.addEventListener('click', () => changeLine(key, Math.max(0, (parseInt(qtyEl.value, 10) || 1) - 1)));
      $('[data-line-remove]', line)?.addEventListener('click', () => changeLine(key, 0));
    });
  }
  function openCart() {
    if (!drawer) return;
    drawer.hidden = false; requestAnimationFrame(() => drawer.classList.add('is-open'));
    document.body.classList.add('no-scroll'); releaseCart = trap(drawer);
    ($('[data-cart-close]', drawer) || drawer).focus();
  }
  function closeCart() {
    if (!drawer) return;
    drawer.classList.remove('is-open'); document.body.classList.remove('no-scroll'); releaseCart && releaseCart();
    const done = () => { drawer.hidden = true; drawer.removeEventListener('transitionend', done); };
    RM ? done() : drawer.addEventListener('transitionend', done);
  }
  $$('[data-cart-open]').forEach((b) => b.addEventListener('click', (e) => { e.preventDefault(); openCart(); }));
  $('[data-cart-close]')?.addEventListener('click', closeCart);
  drawer?.addEventListener('click', (e) => { if (e.target === drawer) closeCart(); });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && drawer && !drawer.hidden) closeCart(); });

  // Intercept product add-to-cart forms
  $$('form[action="/cart/add"], form[action^="/cart/add"]').forEach((form) => {
    form.addEventListener('submit', async (e) => {
      if (form.dataset.noAjax) return;
      e.preventDefault();
      const btn = $('[type=submit]', form); const label = btn && btn.innerHTML;
      if (btn) { btn.disabled = true; btn.classList.add('is-loading'); }
      try {
        const fd = new FormData(form);
        const r = await fetch('/cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body: fd });
        if (!r.ok) { const err = await r.json(); throw new Error(err.description || 'Could not add this service to your booking'); }
        if (btn) { btn.classList.remove('is-loading'); btn.classList.add('is-added'); btn.innerHTML = 'Booked ✓'; }
        await refreshCart(); openCart();
        setTimeout(() => { if (btn) { btn.disabled = false; btn.classList.remove('is-added'); btn.innerHTML = label; } }, 1600);
      } catch (err) {
        if (btn) { btn.disabled = false; btn.classList.remove('is-loading'); }
        const note = form.querySelector('[data-add-error]');
        if (note) { note.textContent = err.message; note.hidden = false; }
      }
    });
  });

  /* ---------- Booking acknowledgement ----------
     The buyer is reserving a service they must bring an item in for, so the
     acknowledgement is a real required control (native validation does the
     enforcing and the screen-reader announcement) and it is mirrored into the
     cart attributes immediately, so it survives however they reach checkout. */
  function syncAckAttribute(checked, value) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        attributes: {
          'Booking acknowledgement': checked ? value : '',
          'Booking type': 'In-person drop-off and collection',
        },
      }),
    }).catch(() => {});
  }

  $$('[data-ack-input]').forEach((box) => {
    const wrap = box.closest('[data-ack-wrap]');
    const setState = (invalid) => {
      if (wrap) wrap.classList.toggle('ack--invalid', invalid);
      box.setAttribute('aria-invalid', invalid ? 'true' : 'false');
    };
    box.addEventListener('invalid', () => setState(true));
    box.addEventListener('change', () => {
      setState(!box.checked);
      if (box.hasAttribute('data-ack-sync')) syncAckAttribute(box.checked, box.value);
    });
  });

  // Keep every acknowledgement checkbox on the page in step with the others.
  document.addEventListener('change', (e) => {
    const el = e.target;
    if (!el || !el.matches || !el.matches('[data-ack-input]')) return;
    $$('[data-ack-input]').forEach((other) => {
      if (other !== el && other.type === 'checkbox' && other.name === el.name) {
        other.checked = el.checked;
        const w = other.closest('[data-ack-wrap]');
        if (w && el.checked) w.classList.remove('ack--invalid');
      }
    });
  });

  if (drawer) refreshCart();
  window.TGS = window.TGS || {}; window.TGS.openCart = openCart; window.TGS.refreshCart = refreshCart; window.TGS.initAccordion = initAccordion;
})();
